import { useEffect, useMemo, useState } from "react"
import {
  ArrowLeft,
  Loader2,
  AlertTriangle,
  ExternalLink,
  Pencil,
  ChevronDown,
  FileText,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  useReconciliationStore,
  decideAllKey,
  RECONCILE_POLL_INTERVAL_MS,
  type ProposalDecisionAction,
  type ProposalNotice,
} from "@/stores/reconciliation-store"
import { useDocumentsStore } from "@/stores/documents-store"
import { usePreviewStore } from "@/stores/preview-store"
import {
  displayCitationPage,
  resolveCitationTargetPage,
  toPrecisePreviewHighlight,
} from "@/lib/citation-preview"
import type { SourceHighlightData } from "@/stores/assistant-store"
import { EXTRACTION_CATEGORY_LABELS, type ExtractionCategory } from "@/types/extraction"
import type {
  ReconciliationProposal,
  ReconciliationProposalKind,
} from "@/lib/schemas"
import { cn } from "@/lib/utils"

// ===================
// Field-label + value humanization
// ===================

/** Small override map for the least-obvious payload keys (spec). */
const FIELD_LABEL_OVERRIDES: Record<string, string> = {
  date_iso: "Date",
  page_word_limit: "Page/word limit",
  time_text: "Time",
  ref_clause: "Reference",
  impact_area: "Impact area",
}

/**
 * Provenance + internal columns that live in a proposal's payload (an `add`
 * carries the full mapped candidate, incl. the four sourceRef columns) but must
 * never render as user-facing field lines. The verbatim quote + page already
 * show in the dedicated evidence block; `source_highlight_data` in particular is
 * a large rect-array JSON blob that is pure noise on screen.
 */
const HIDDEN_FIELD_KEYS = new Set<string>([
  "source_quote",
  "source_page_number",
  "source_document_id",
  "source_highlight_data",
  "response_expectation",
  "response_expectation_source",
  "parent_id",
])

export function humanizeFieldLabel(key: string): string {
  if (FIELD_LABEL_OVERRIDES[key]) return FIELD_LABEL_OVERRIDES[key]
  return key
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
}

/** Render an arbitrary payload/target value as a display string. */
export function formatFieldValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—"
  if (typeof value === "boolean") return value ? "Yes" : "No"
  if (typeof value === "string" || typeof value === "number") return String(value)
  if (Array.isArray(value)) return value.map((v) => formatFieldValue(v)).join(", ")
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

/** Best-effort primary label (title / event / …) for a proposal row. */
const TITLE_KEYS = ["title", "event", "requirement", "criterion", "name", "item", "risk"]

function proposalTitle(proposal: ReconciliationProposal): string {
  const source = { ...(proposal.target?.currentValues ?? {}), ...(proposal.payload ?? {}) }
  for (const key of TITLE_KEYS) {
    const v = source[key]
    if (typeof v === "string" && v.trim()) return v
  }
  return EXTRACTION_CATEGORY_LABELS[proposal.category as ExtractionCategory] ?? proposal.category
}

const KIND_META: Record<
  ReconciliationProposalKind,
  { label: string; variant: "success" | "warning" | "destructive" }
> = {
  add: { label: "New", variant: "success" },
  change: { label: "Changed", variant: "warning" },
  remove: { label: "Removed", variant: "destructive" },
}

// ===================
// Evidence quote block (reuses ExtractionTable's source-preview wiring)
// ===================

interface EvidenceQuoteProps {
  documentId: string | null
  page: number | null
  quote: string
  highlightData: SourceHighlightData | null
  citationId: string
}

function EvidenceQuote({ documentId, page, quote, highlightData, citationId }: EvidenceQuoteProps) {
  const openPreview = usePreviewStore((s) => s.openPreview)

  const handleOpen = () => {
    if (!documentId) return
    const precise = toPrecisePreviewHighlight(highlightData, quote, citationId)
    const targetPage = resolveCitationTargetPage(
      precise?.highlight ? { highlight: precise.highlight } : undefined,
      page
    )
    void openPreview({
      documentId,
      // Explicit 'library' (= "modal, no dock"): the review surface renders no
      // docked reader, and openPreview otherwise INHERITS the previous context
      // for a still-cached document — a stale 'analysis' would self-hide the
      // modal and make this click do nothing.
      context: 'library',
      ...(targetPage != null ? { page: targetPage } : {}),
      ...(precise?.highlight ? { highlight: precise.highlight } : {}),
      ...(precise?.textHighlight ? { textHighlight: precise.textHighlight } : {}),
    })
  }

  const clickable = Boolean(documentId)
  const shownPage = displayCitationPage(page)
  return (
    <button
      type="button"
      onClick={handleOpen}
      disabled={!clickable}
      className={cn(
        "mt-2 flex w-full flex-col gap-1 rounded-r-md border-l-2 border-[hsl(var(--brand))] bg-muted/30 px-3 py-2 text-left",
        clickable && "hover:bg-muted/50 transition-colors"
      )}
      title={clickable ? "Open source in document" : undefined}
    >
      <span className="text-meta italic line-clamp-3">“{quote}”</span>
      {(shownPage != null || clickable) && (
        <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
          {shownPage != null ? `p.${shownPage}` : "Source"}
          {clickable && <ExternalLink className="h-3 w-3" />}
        </span>
      )}
    </button>
  )
}

// ===================
// Evidence extraction (camelCase keys: quote / pageNumber / highlightData)
// ===================

function getQuote(proposal: ReconciliationProposal): string | null {
  const q = proposal.evidence?.["quote"]
  return typeof q === "string" && q.trim() ? q : null
}

function getPage(proposal: ReconciliationProposal): number | null {
  const p = proposal.evidence?.["pageNumber"]
  return typeof p === "number" ? p : null
}

function getHighlightData(proposal: ReconciliationProposal): SourceHighlightData | null {
  const h = proposal.evidence?.["highlightData"]
  return (h ?? null) as SourceHighlightData | null
}

// ===================
// Change-diff computation
// ===================

interface DiffLine {
  key: string
  label: string
  oldValue: string
  newValue: string
}

function computeDiffLines(proposal: ReconciliationProposal): DiffLine[] {
  const payload = proposal.payload ?? {}
  const current = proposal.target?.currentValues ?? {}
  const lines: DiffLine[] = []
  for (const key of Object.keys(payload)) {
    if (HIDDEN_FIELD_KEYS.has(key)) continue
    const newValue = formatFieldValue(payload[key])
    const oldValue = formatFieldValue(current[key])
    if (newValue === oldValue) continue
    lines.push({ key, label: humanizeFieldLabel(key), oldValue, newValue })
  }
  return lines
}

/** Fields to summarise for an `add` proposal (skip evidence-y keys). */
function addSummaryLines(proposal: ReconciliationProposal): DiffLine[] {
  const payload = proposal.payload ?? {}
  return Object.keys(payload)
    .filter((k) => !TITLE_KEYS.includes(k) && !HIDDEN_FIELD_KEYS.has(k))
    .map((key) => ({
      key,
      label: humanizeFieldLabel(key),
      oldValue: "",
      newValue: formatFieldValue(payload[key]),
    }))
    .filter((l) => l.newValue !== "—")
}

// ===================
// Component
// ===================

interface AddendumReviewViewProps {
  bidId: string
  runId: string
  onBack: () => void
  readOnly?: boolean | undefined
}

export function AddendumReviewView({ bidId, runId, onBack, readOnly }: AddendumReviewViewProps) {
  const loadRunDetail = useReconciliationStore((s) => s.loadRunDetail)
  const decideProposal = useReconciliationStore((s) => s.decideProposal)
  const decideAll = useReconciliationStore((s) => s.decideAll)
  const detail = useReconciliationStore((s) => s.runDetailByRun[runId])
  const isLoading = useReconciliationStore((s) => s.runDetailLoading[runId])
  const error = useReconciliationStore((s) => s.runDetailError[runId])
  const decidingProposals = useReconciliationStore((s) => s.decidingProposals)
  const decidingAll = useReconciliationStore((s) => s.decidingAll)
  const proposalNotices = useReconciliationStore((s) => s.proposalNotices)
  const documents = useDocumentsStore((s) => s.documents)

  useEffect(() => {
    void loadRunDetail(bidId, runId)
  }, [bidId, runId, loadRunDetail])

  const run = detail?.run
  const status = run?.status

  // Carried obligation A: a deep-linked user viewing a queued/running run must
  // see it land in 'review' without a manual refresh. Poll the detail while the
  // run is active; the interval clears the moment status leaves queued/running
  // (the last fetch that returned a terminal status already carries proposals).
  useEffect(() => {
    if (status !== "queued" && status !== "running") return
    const id = setInterval(() => {
      void loadRunDetail(bidId, runId)
    }, RECONCILE_POLL_INTERVAL_MS.value)
    return () => clearInterval(id)
  }, [status, bidId, runId, loadRunDetail])
  const proposals = useMemo(() => detail?.proposals ?? [], [detail])

  const docLabel = useMemo(() => {
    if (run) {
      const doc = documents.find((d) => d.id === run.documentId)
      if (doc) return doc.name
    }
    return proposals.find((p) => p.sourceDocumentLabel)?.sourceDocumentLabel ?? "addendum"
  }, [run, documents, proposals])

  const pending = useMemo(() => proposals.filter((p) => p.status === "pending"), [proposals])
  const decided = useMemo(() => proposals.filter((p) => p.status !== "pending"), [proposals])

  const counts = useMemo(() => {
    const c = { add: 0, change: 0, remove: 0 }
    for (const p of pending) c[p.kind]++
    return c
  }, [pending])

  // Pending grouped by category, preserving category order.
  const groups = useMemo(() => {
    const byCat = new Map<string, ReconciliationProposal[]>()
    for (const p of pending) {
      const list = byCat.get(p.category) ?? []
      list.push(p)
      byCat.set(p.category, list)
    }
    return Array.from(byCat.entries())
  }, [pending])

  const handleDecide = (
    proposalId: string,
    action: ProposalDecisionAction,
    expectedTargetVersion?: string
  ) => {
    void decideProposal(bidId, runId, proposalId, action, expectedTargetVersion)
  }
  const handleDecideAll = (action: ProposalDecisionAction, category?: string) => {
    void decideAll(bidId, runId, action, category)
  }

  // ── Loading / error / progress / failed states ──
  if (isLoading && !detail) {
    return (
      <ReviewShell onBack={onBack} title="Review changes">
        <div className="flex items-center gap-2 py-12 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading changes…
        </div>
      </ReviewShell>
    )
  }

  if (error && !detail) {
    return (
      <ReviewShell onBack={onBack} title="Review changes">
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <AlertTriangle className="h-6 w-6 text-destructive" />
          <p className="text-sm text-muted-foreground">Couldn’t load this review. {error}</p>
          <Button size="sm" variant="outline" onClick={() => void loadRunDetail(bidId, runId)}>
            Try again
          </Button>
        </div>
      </ReviewShell>
    )
  }

  if (!run) {
    return (
      <ReviewShell onBack={onBack} title="Review changes">
        <p className="py-12 text-center text-sm text-muted-foreground">This review is unavailable.</p>
      </ReviewShell>
    )
  }

  if (run.status === "queued" || run.status === "running") {
    return (
      <ReviewShell onBack={onBack} title={`Review changes — ${docLabel}`}>
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--ai-accent-text))]" />
          <p className="text-sm text-muted-foreground">
            Analysing the addendum for changes… this can take a minute.
          </p>
        </div>
      </ReviewShell>
    )
  }

  if (run.status === "failed") {
    return (
      <ReviewShell onBack={onBack} title={`Review changes — ${docLabel}`}>
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <AlertTriangle className="h-6 w-6 text-destructive" />
          <p className="text-sm text-muted-foreground">
            {run.error ?? "Reconciliation failed."}
          </p>
          <p className="text-meta">
            Re-upload the addendum or start the review again from the Analysis banner.
          </p>
        </div>
      </ReviewShell>
    )
  }

  // ── Honest-empty: a completed run with no structured proposals ──
  if (proposals.length === 0) {
    const doc = documents.find((d) => d.id === run.documentId)
    return (
      <ReviewShell onBack={onBack} title={`Review changes — ${docLabel}`}>
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <FileText className="h-6 w-6 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            We couldn’t extract structured changes — review the addendum directly.
          </p>
          {doc && (
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                void usePreviewStore
                  .getState()
                  .openPreview({ documentId: doc.id, context: 'library' })
              }
            >
              Open {doc.name}
            </Button>
          )}
          {!readOnly && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleDecideAll("dismiss")}
            >
              Dismiss run
            </Button>
          )}
        </div>
      </ReviewShell>
    )
  }

  const totalPending = pending.length
  // Whole-run dismiss-all busy key — disables every control while it runs.
  const runBusy = Boolean(decidingAll[decideAllKey(runId, undefined)])

  return (
    <ReviewShell
      onBack={onBack}
      title={`Review changes — ${docLabel}`}
      headerRight={
        <div className="flex items-center gap-3">
          {totalPending > 0 && (
            <span className="text-meta whitespace-nowrap" data-testid="pending-counts">
              {counts.add} new · {counts.change} changed · {counts.remove} removed
            </span>
          )}
          {!readOnly && totalPending > 0 && (
            <ConfirmButton
              label="Dismiss all"
              disabled={runBusy}
              title="Dismiss all changes?"
              description="All pending proposals from this addendum will be discarded. This can’t be undone."
              confirmLabel="Dismiss all"
              onConfirm={() => handleDecideAll("dismiss")}
            />
          )}
        </div>
      }
    >
      {totalPending === 0 && (
        <p className="py-8 text-center text-sm text-muted-foreground">
          All changes reviewed.
        </p>
      )}

      {groups.map(([category, items]) => {
        const catBusy = runBusy || Boolean(decidingAll[decideAllKey(runId, category)])
        // Carried obligation B: accepting a category overwrites any items your
        // team already edited — confirm (and say how many) before firing.
        const touchedCount = items.filter((p) => p.target?.userTouched).length
        return (
          <section key={category} className="space-y-2">
            <div className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-2">
              <p className="text-sm font-medium">
                {EXTRACTION_CATEGORY_LABELS[category as ExtractionCategory] ?? category} ({items.length}{" "}
                pending)
              </p>
              {!readOnly &&
                (touchedCount > 0 ? (
                  <ConfirmButton
                    trigger={
                      <Button size="sm" variant="ai-ghost" disabled={catBusy}>
                        Accept all
                      </Button>
                    }
                    title="Accept all changes in this category?"
                    description={`${touchedCount} ${
                      touchedCount === 1 ? "item was" : "items were"
                    } edited by your team — accepting will overwrite ${
                      touchedCount === 1 ? "that edit" : "those edits"
                    }.`}
                    confirmLabel="Accept all"
                    onConfirm={() => handleDecideAll("accept", category)}
                  />
                ) : (
                  <Button
                    size="sm"
                    variant="ai-ghost"
                    disabled={catBusy}
                    onClick={() => handleDecideAll("accept", category)}
                  >
                    Accept all
                  </Button>
                ))}
            </div>
            <div className="space-y-2">
              {items.map((proposal) => (
                <ProposalRow
                  key={proposal.id}
                  proposal={proposal}
                  readOnly={readOnly}
                  busy={catBusy || Boolean(decidingProposals[proposal.id])}
                  notice={proposalNotices[proposal.id]}
                  onDecide={handleDecide}
                />
              ))}
            </div>
          </section>
        )
      })}

      {decided.length > 0 && (
        <Collapsible className="mt-4">
          <CollapsibleTrigger className="group flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
            Decided ({decided.length})
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2 space-y-1">
            {decided.map((proposal) => (
              <div
                key={proposal.id}
                className="flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <Badge variant={KIND_META[proposal.kind].variant} className="shrink-0">
                    {KIND_META[proposal.kind].label}
                  </Badge>
                  <span className="truncate text-muted-foreground">{proposalTitle(proposal)}</span>
                </span>
                <Badge
                  variant={proposal.status === "accepted" ? "success" : "default"}
                  className="shrink-0 capitalize"
                  dot={proposal.status === "accepted"}
                >
                  {proposal.status}
                </Badge>
              </div>
            ))}
          </CollapsibleContent>
        </Collapsible>
      )}
    </ReviewShell>
  )
}

// ===================
// Proposal row
// ===================

// ── Inline row notices (Task 3: stale/orphaned/apply_failed/skipped) ──

const SKIP_REASON_TEXT: Record<string, string> = {
  orphaned: "This item no longer exists — nothing to update.",
  stale: "This item changed since the addendum — re-review before deciding.",
  alreadyDecided: "Already decided elsewhere.",
  apply_failed: "Couldn’t apply — please try again.",
}

export function noticeText(notice: ProposalNotice): string {
  switch (notice.kind) {
    case "stale":
      return "This item changed — re-review before deciding."
    case "orphaned":
      return "This item no longer exists — it may have been removed."
    case "apply_failed":
      return "Couldn’t apply this change — it was reverted. Try again."
    case "skipped":
      return (notice.reason && SKIP_REASON_TEXT[notice.reason]) || "Skipped — please re-review."
  }
}

function ProposalNoticeLine({ notice }: { notice: ProposalNotice }) {
  return (
    <p
      role="status"
      className="flex items-center gap-1 text-xs text-[hsl(var(--status-warning))]"
    >
      <AlertTriangle className="h-3 w-3 shrink-0" />
      {noticeText(notice)}
    </p>
  )
}

interface ProposalRowProps {
  proposal: ReconciliationProposal
  readOnly?: boolean | undefined
  busy?: boolean | undefined
  notice?: ProposalNotice | undefined
  onDecide: (
    proposalId: string,
    action: ProposalDecisionAction,
    expectedTargetVersion?: string
  ) => void
}

function ProposalRow({ proposal, readOnly, busy, notice, onDecide }: ProposalRowProps) {
  const meta = KIND_META[proposal.kind]
  const title = proposalTitle(proposal)
  const quote = getQuote(proposal)
  const page = getPage(proposal)
  const isOrphaned =
    (proposal.kind === "change" || proposal.kind === "remove") && !proposal.target
  const diffLines = proposal.kind === "change" ? computeDiffLines(proposal) : []
  // A `change` whose payload matches the current values renders no diff lines —
  // there's nothing to accept, so offer Dismiss only.
  const noEffectiveChange =
    proposal.kind === "change" && !isOrphaned && diffLines.length === 0
  const canAccept = !isOrphaned && !noEffectiveChange
  const userTouched = Boolean(proposal.target?.userTouched)
  // Re-baseline after a stale 409: the detail was refreshed so the row's
  // `target.updatedAt` is now the live version. Send it as expectedTargetVersion
  // so the retry guards against what the user just re-reviewed, not the frozen
  // proposal version (which would 409 stale forever).
  const staleRetryVersion =
    notice?.kind === "stale" ? proposal.target?.updatedAt : undefined
  const acceptProposal = () => onDecide(proposal.id, "accept", staleRetryVersion)

  return (
    <div className="rounded-xl border border-border bg-card p-3 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={meta.variant}>{meta.label}</Badge>
            <span
              className={cn(
                "text-sm font-medium",
                proposal.kind === "remove" && "line-through text-muted-foreground"
              )}
            >
              {title}
            </span>
            {proposal.target?.userTouched && (
              <Badge variant="default" dot={false} className="gap-1">
                <Pencil className="h-3 w-3" />
                Edited by your team
              </Badge>
            )}
          </div>

          {isOrphaned ? (
            <p className="text-meta italic">
              This item no longer exists — it may have been removed since the addendum was analysed.
            </p>
          ) : (
            <>
              {proposal.kind === "change" &&
                (noEffectiveChange ? (
                  <p className="text-meta italic">
                    No effective change — the addendum matches the current values.
                  </p>
                ) : (
                  <div className="space-y-1">
                    {diffLines.map((line) => (
                      <div key={line.key} className="text-xs">
                        <span className="text-muted-foreground">{line.label}: </span>
                        <span className="text-muted-foreground line-through">{line.oldValue}</span>
                        <span className="mx-1 text-muted-foreground">→</span>
                        <span className="font-medium">{line.newValue}</span>
                      </div>
                    ))}
                  </div>
                ))}
              {proposal.kind === "add" && (
                <div className="space-y-0.5">
                  {addSummaryLines(proposal).map((line) => (
                    <div key={line.key} className="text-xs">
                      <span className="text-muted-foreground">{line.label}: </span>
                      <span>{line.newValue}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {quote && (
            <EvidenceQuote
              documentId={proposal.sourceDocumentId ?? null}
              page={page}
              quote={quote}
              highlightData={getHighlightData(proposal)}
              citationId={`proposal-${proposal.id}`}
            />
          )}

          {notice && <ProposalNoticeLine notice={notice} />}
        </div>

        {!readOnly && (
          <div className="flex shrink-0 items-center gap-2">
            {canAccept &&
              (userTouched ? (
                <ConfirmButton
                  trigger={
                    <Button size="sm" disabled={busy}>
                      Accept
                    </Button>
                  }
                  title="Accept this change?"
                  description="Your team edited this item — accepting updates it."
                  confirmLabel="Accept"
                  onConfirm={acceptProposal}
                />
              ) : (
                <Button size="sm" disabled={busy} onClick={acceptProposal}>
                  Accept
                </Button>
              ))}
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => onDecide(proposal.id, "dismiss")}
            >
              Dismiss
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

// ===================
// Shell + confirm button helpers
// ===================

interface ReviewShellProps {
  onBack: () => void
  title: string
  headerRight?: React.ReactNode
  children: React.ReactNode
}

function ReviewShell({ onBack, title, headerRight, children }: ReviewShellProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={onBack} aria-label="Back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-sm font-medium truncate">{title}</h2>
        </div>
        {headerRight}
      </div>
      {children}
    </div>
  )
}

interface ConfirmButtonProps {
  /** Default ghost trigger label. Ignored when `trigger` is supplied. */
  label?: string
  /** Custom trigger element (asChild needs a single element) — lets callers style the button (e.g. Accept). */
  trigger?: React.ReactElement
  title: string
  description: string
  confirmLabel: string
  onConfirm: () => void
  disabled?: boolean
}

function ConfirmButton({
  label,
  trigger,
  title,
  description,
  confirmLabel,
  onConfirm,
  disabled,
}: ConfirmButtonProps) {
  const [open, setOpen] = useState(false)
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        {trigger ?? (
          <Button size="sm" variant="ghost" disabled={disabled}>
            {label}
          </Button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault()
              onConfirm()
              setOpen(false)
            }}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
